import { z } from 'zod';
import { isSignalTopicKey } from './signal-topics.js';
/**
 * ExternalSignal — a single industry/region-relevant news/event item
 * surfaced into the org. The connector pipeline (Phase 5: real
 * news/RSS adapters; v0: deterministic mock generator) writes these
 * server-side; clients only read.
 *
 * Per-org scope means each tenant gets its own filtered view of the
 * world even when the underlying firehose is shared. The
 * `BusinessContext.signalTopics` array is the subscription list —
 * signals whose `topic` isn't in that set never get written for the
 * org in the first place.
 *
 * IDs are deterministic where possible (`${orgId}-${topic}-${index}`
 * for the v0 mock) so re-running the seeder is idempotent. Real
 * adapters will mint a sourceUrl-derived hash so duplicate events
 * don't double-write.
 */
export const EXTERNAL_SIGNALS_COLLECTION = 'externalSignals';
/**
 * Signal severity. `info` = passive notice; `watch` = something the
 * CEO should be tracking; `critical` = action-now territory. Drives
 * dot color in the right-rail watchlist tab + filter chips on
 * `/signals/external`.
 */
export const SignalSeverity = z.enum(['info', 'watch', 'critical']);
export const ExternalSignal = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /**
     * Topic key — must match a `SignalTopicKey` from `signal-topics.ts`.
     * The watchlist + page filters group by topic, and the
     * personalization layer can promote signals whose topic is
     * cross-referenced in `BusinessContext.topPriorities`.
     *
     * Refined (rather than Zod-enum) so the constraint is enforced at
     * parse time without us having to keep two enum literals in sync
     * across files. Adding a topic to `SIGNAL_TOPICS` immediately
     * lets it pass here.
     */
    topic: z.string().min(1).refine(isSignalTopicKey, {
        message: 'topic must be a known SignalTopicKey from signal-topics.ts',
    }),
    /** Source name, displayed verbatim in the source-badge slot. */
    source: z.string().min(1),
    sourceUrl: z.string().url().optional(),
    title: z.string().min(1),
    summary: z.string(),
    severity: SignalSeverity.default('info'),
    /**
     * 0..1 score the connector's relevance ranker assigns. Phase 5 ML
     * model; v0 mock uses a deterministic spread so the chart still
     * has visual variety. Used to sort the feed when no explicit sort
     * is requested.
     */
    relevanceScore: z.number().min(0).max(1).default(0.5),
    /** ISO timestamp from the source. */
    publishedAt: z.string(),
    /** ISO timestamp when we ingested. */
    ingestedAt: z.string(),
    /** Optional ISO TTL — Firestore TTL field (when wired). */
    expiresAt: z.string().optional(),
    /** Free-form tags for additional faceting (e.g. peer-company name). */
    tags: z.array(z.string()).default([]),
});
/** Wire shape returned by `GET /api/orgs/:o/external-signals`. */
export const ListExternalSignalsResponse = z.object({
    signals: z.array(ExternalSignal),
});
